import React from "react";
import { Truck, MapPin } from "lucide-react";
import { DeliveryOption } from "../../data/deliveryConfig";

interface Props {
  options: DeliveryOption[];
  selectedId: string;
  /** Precio de delivery actualmente elegido (para resaltar chips de rango) */
  selectedPrice?: number;
  onChange: (option: DeliveryOption, price: number) => void;
  compact?: boolean;
}

const DeliveryZoneSelector: React.FC<Props> = ({
  options,
  selectedId,
  selectedPrice,
  onChange,
  compact = false,
}) => {
  const selectedOption = options.find((o) => o.id === selectedId);
  const showPriceChips =
    selectedOption &&
    selectedOption.type === "delivery" &&
    selectedOption.prices.length > 1;

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-1.5 text-[11px] font-bold text-rose-950 uppercase tracking-wider">
        <Truck className="w-3 h-3 text-rose-600" />
        {compact ? "Entrega" : "Zona de Entrega"}
      </label>

      <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-2"} gap-1.5`}>
        {options.map((opt) => {
          const isSelected = opt.id === selectedId;
          const isPickup = opt.type === "pickup";
          const isRange = opt.type === "delivery" && opt.prices.length > 1;

          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt, opt.price)}
              className={`p-2.5 rounded-xl border-2 text-left transition-all flex items-center justify-between gap-2 ${
                isSelected
                  ? "bg-rose-50/80 border-rose-600 ring-2 ring-rose-100 shadow-sm"
                  : "bg-white border-rose-100/80 hover:border-rose-300 hover:bg-rose-50/30"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {isPickup ? (
                  <MapPin className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? "text-rose-600" : "text-rose-400"}`} />
                ) : (
                  <Truck className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? "text-rose-600" : "text-rose-400"}`} />
                )}
                <div className="min-w-0">
                  <p className={`font-body text-xs truncate ${isSelected ? "text-rose-950 font-bold" : "text-gray-700 font-medium"}`}>
                    {opt.label}
                  </p>
                  {!compact && isPickup && opt.address && (
                    <span className="text-[9px] text-gray-400">{opt.address}</span>
                  )}
                </div>
              </div>

              <div className="text-right flex-shrink-0">
                <span className={`font-display text-xs font-bold ${isSelected ? "text-rose-700" : "text-rose-900"}`}>
                  {isPickup ? (
                    "Gratis"
                  ) : (
                    <>
                      <span className="text-[9px] font-normal text-rose-500">Ref</span>{" "}
                      {isRange ? `${opt.prices[0]}-${opt.prices[opt.prices.length - 1]}` : opt.price}
                    </>
                  )}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {showPriceChips && (
        <div className="pt-1">
          <p className="text-[10px] font-bold text-rose-950 uppercase tracking-wider mb-1.5">
            Elige el precio de delivery
          </p>
          <div className="flex flex-wrap gap-1.5">
            {selectedOption!.prices.map((p) => {
              const isActive = selectedPrice === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => onChange(selectedOption!, p)}
                  className={`px-3 py-1.5 rounded-full border-2 text-xs font-display font-bold transition-all ${
                    isActive
                      ? "bg-rose-600 border-rose-600 text-white shadow-sm"
                      : "bg-white border-rose-200 text-rose-900 hover:border-rose-400"
                  }`}
                >
                  Ref {p}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryZoneSelector;